import {
  closestCenter,
  DndContext,
  DragEndEvent,
  DragOverlay,
  DragStartEvent,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core'
import { restrictToVerticalAxis } from '@dnd-kit/modifiers'
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import {
  type ClientModel,
  type ProjectInventoryItemModel,
  type ProjectModel,
} from '@itemize/server/models'
import {
  Add as AddIcon,
  Close as CloseIcon,
  DragIndicator as DragIndicatorIcon,
  FileDownload as FileDownloadIcon,
} from '@mui/icons-material'
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  IconButton,
  Paper,
  SxProps,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableFooter,
  TableHead,
  TableRow,
  TextField,
  Theme,
  Typography,
} from '@mui/material'
import { FC, KeyboardEvent, useEffect, useRef, useState } from 'react'
import { api, InventoryItemFormData } from '../../redux/api.ts'
import { AddInventoryItemsModal } from '../../components/add-inventory-items-modal.tsx'
import { ConfirmDialog } from '../../components/confirm-dialog.tsx'
import { API_BASE_URL } from '../../config.ts'
import { useAuth } from '../../contexts/auth-context.tsx'
import { MoneyInput } from '../../components/money-input.tsx'
import { InventoryItemFormModal } from '../../components/inventory-item-form-modal.tsx'
import { useToggle } from '../../hooks/use-toggle.ts'
import type { InventoryItemModel } from '@itemize/server/models'

// Extended type that includes inventory item details
interface ProjectItemWithDetails extends ProjectInventoryItemModel {
  name: string
  description: string
  originalPrice: number
  currentPrice: number
}

interface EditableFieldProps {
  value: number
  onSubmit: (value: number) => void
  type: 'currency' | 'number'
  sx?: SxProps<Theme>
  disabled?: boolean
}

const EditableField: FC<EditableFieldProps> = ({ value, onSubmit, type, sx, disabled = false }) => {
  const [isEditing, setIsEditing] = useState(false)
  const [editValue, setEditValue] = useState(value.toString())

  const handleClick = () => {
    if (!disabled) {
      setIsEditing(true)
      setEditValue(value.toString())
    }
  }

  const handleSubmit = () => {
    const numValue = parseFloat(editValue)
    if (!isNaN(numValue) && numValue >= 0) {
      onSubmit(numValue)
    }
    setIsEditing(false)
  }

  const handleKeyPress = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      handleSubmit()
    } else if (event.key === 'Escape') {
      setIsEditing(false)
    }
  }

  const handleBlur = () => {
    handleSubmit()
  }

  // For currency type, use MoneyInput with cents
  if (isEditing && type === 'currency') {
    // Convert dollars to cents for MoneyInput
    const valueInCents = Math.round(value * 100)
    return (
      <MoneyInput
        value={valueInCents}
        onChange={(cents) => {
          // Convert cents back to dollars for onSubmit
          onSubmit(cents / 100)
          setIsEditing(false)
        }}
        onBlur={() => setIsEditing(false)}
        size="small"
        autoFocus
        sx={sx}
      />
    )
  }

  if (isEditing) {
    return (
      <TextField
        value={editValue}
        onChange={(e) => setEditValue(e.target.value)}
        onKeyDown={handleKeyPress}
        onBlur={handleBlur}
        autoFocus
        size="small"
        type="number"
        slotProps={{
          htmlInput: {
            min: 0,
            step: 1,
          },
        }}
      />
    )
  }

  return (
    <Box
      onClick={handleClick}
      sx={{
        cursor: disabled ? 'default' : 'pointer',
        padding: '4px 8px',
        borderRadius: 1,
        opacity: disabled ? 0.6 : 1,
        '&:hover': {
          backgroundColor: disabled ? 'transparent' : 'action.hover',
        },
      }}
    >
      <Typography variant="body2">
        {type === 'currency' ? `$${value.toFixed(2)}` : value}
      </Typography>
    </Box>
  )
}

interface ProjectItemRowProps {
  item: ProjectItemWithDetails
  onUpdateItem: (id: string, updates: { quantity?: number; priceCents?: number }) => void
  onDeleteItem: (id: string) => void
  isUpdating?: boolean
}

const ProjectItemRow: FC<ProjectItemRowProps> = ({
  item,
  onUpdateItem,
  onDeleteItem,
  isUpdating = false,
}) => {
  const [isHovered, setIsHovered] = useState(false)
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)

  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: item.id,
  })

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.6 : 1,
    backgroundColor: isDragging ? 'rgba(0, 0, 0, 0.05)' : 'transparent',
  }

  const subtotal = item.currentPrice * item.quantity

  const handleQuantityUpdate = (quantity: number) => {
    onUpdateItem(item.id, { quantity })
  }

  const handlePriceUpdate = (price: number) => {
    // Price is already in dollars from EditableField, convert to cents
    const priceCents = Math.round(price * 100)
    onUpdateItem(item.id, { priceCents })
  }

  const handleDeleteClick = () => {
    setShowDeleteConfirm(true)
  }

  const handleConfirmDelete = () => {
    onDeleteItem(item.id)
    setShowDeleteConfirm(false)
  }

  return (
    <>
      <TableRow
        ref={setNodeRef}
        style={style}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        sx={{
          position: 'relative',
          '&:hover': {
            backgroundColor: 'action.hover',
          },
          ...(isDragging && {
            backgroundColor: 'grey.100',
            '& *': {
              color: 'grey.500',
            },
          }),
        }}
        {...attributes}
      >
        <TableCell sx={{ padding: '8px' }}>
          {isHovered && (
            <IconButton
              size="small"
              sx={{ color: 'text.secondary', cursor: 'grab' }}
              {...listeners}
            >
              <DragIndicatorIcon />
            </IconButton>
          )}
        </TableCell>
        <TableCell sx={{ position: 'relative', width: '60px', padding: '8px' }}>
          <Box
            component="img"
            src={item.imageUrl || '/images/image-placeholder.jpg'}
            alt={item.name}
            sx={{
              width: 50,
              height: 50,
              objectFit: 'cover',
              borderRadius: 1,
            }}
          />
        </TableCell>
        <TableCell sx={{ padding: '8px' }}>
          <Typography variant="body2" fontWeight="medium">
            {item.name}
          </Typography>
        </TableCell>
        <TableCell sx={{ padding: '8px' }}>
          <Typography variant="body2" color="text.secondary">
            {item.description}
          </Typography>
        </TableCell>
        <TableCell sx={{ padding: '8px', textAlign: 'right' }}>
          <EditableField
            value={item.currentPrice}
            onSubmit={handlePriceUpdate}
            type="currency"
            disabled={isUpdating}
          />
        </TableCell>
        <TableCell sx={{ padding: '8px', textAlign: 'right' }}>
          <EditableField
            value={item.quantity}
            onSubmit={handleQuantityUpdate}
            type="number"
            disabled={isUpdating}
          />
        </TableCell>
        <TableCell sx={{ padding: '8px', textAlign: 'right' }}>
          <Typography variant="body2" fontWeight="medium">
            ${subtotal.toFixed(2)}
          </Typography>
        </TableCell>
        <TableCell sx={{ padding: '8px' }}>
          {isHovered && (
            <IconButton
              size="small"
              onClick={handleDeleteClick}
              sx={{ color: 'text.secondary' }}
              disabled={isUpdating}
            >
              <CloseIcon />
            </IconButton>
          )}
        </TableCell>
      </TableRow>

      <ConfirmDialog
        open={showDeleteConfirm}
        onClose={() => setShowDeleteConfirm(false)}
        onConfirm={handleConfirmDelete}
        title="Remove Item"
        message={`Are you sure you want to remove "${item.name}" from this project?`}
        confirmText="Remove"
      />
    </>
  )
}

interface ProjectItemsTableProps {
  project: ProjectModel
  client: ClientModel
}

export const ProjectItemsTable: FC<ProjectItemsTableProps> = ({ project }) => {
  const { getAuthHeader, logout } = useAuth()
  const [showAddItemsModal, setShowAddItemsModal] = useState(false)
  const { isOpen: isCreateItemModalOpen, toggle: toggleCreateItemModal } = useToggle()
  const [activeId, setActiveId] = useState<string | null>(null)
  const [optimisticItems, setOptimisticItems] = useState<ProjectItemWithDetails[]>([])
  const [isOptimisticUpdate, setIsOptimisticUpdate] = useState(false)
  const [isExporting, setIsExporting] = useState(false)
  const previousBaseItemsRef = useRef<ProjectItemWithDetails[]>([])
  const isInitializedRef = useRef(false)

  // API hooks
  const {
    data: projectItems = [],
    isLoading: isLoadingProjectItems,
    error: projectItemsError,
  } = api.useGetProjectInventoryItemsQuery(project.id)

  const { data: inventoryItems = [], isLoading: isLoadingInventoryItems } =
    api.useGetInventoryItemsQuery()

  const [updateProjectInventoryItem, { isLoading: isUpdating }] =
    api.useUpdateProjectInventoryItemMutation()
  const [deleteProjectInventoryItem, { isLoading: isDeleting }] =
    api.useDeleteProjectInventoryItemMutation()
  const [bulkAddProjectInventoryItems, { isLoading: isAdding }] =
    api.useBulkAddProjectInventoryItemsMutation()
  const [updateProjectInventoryItemSortOrder] = api.useUpdateProjectInventoryItemSortOrderMutation()
  const [createInventoryItem] = api.useCreateInventoryItemMutation()

  // Drag and drop sensors
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  )

  // Create a map of inventory items for quick lookup
  const inventoryItemsMap = inventoryItems.reduce(
    (acc, item) => {
      acc[item.id] = item
      return acc
    },
    {} as Record<string, (typeof inventoryItems)[0]>,
  )

  // Transform API data to include computed fields and sort by sortOrder
  const baseItems: ProjectItemWithDetails[] = projectItems
    .map((item) => {
      const inventoryItem = inventoryItemsMap[item.inventoryItemId]
      return {
        ...item,
        name: inventoryItem?.name || 'Unknown Item',
        description: inventoryItem?.description || '',
        originalPrice: item.originalPriceCents / 100,
        currentPrice: (item.priceCents ?? item.originalPriceCents) / 100,
      }
    })
    .sort((a, b) => a.sortOrder - b.sortOrder)

  // Update optimistic items when base items change (after API response)
  useEffect(() => {
    // Initialize on first load
    if (!isInitializedRef.current) {
      setOptimisticItems(baseItems)
      previousBaseItemsRef.current = baseItems
      isInitializedRef.current = true
      return
    }

    // Check if baseItems actually changed (not just a re-render)
    const hasChanged = JSON.stringify(previousBaseItemsRef.current) !== JSON.stringify(baseItems)

    if (hasChanged) {
      setOptimisticItems(baseItems)
      previousBaseItemsRef.current = baseItems
      // Reset optimistic flag when we get fresh data from server
      if (isOptimisticUpdate) {
        setIsOptimisticUpdate(false)
      }
    }
  }, [baseItems, isOptimisticUpdate])

  // Use optimistic items if we have them, otherwise use base items
  const items = isOptimisticUpdate ? optimisticItems : baseItems

  const isLoading = isLoadingProjectItems || isLoadingInventoryItems
  const error = projectItemsError

  const handleUpdateItem = async (
    id: string,
    updates: { quantity?: number; priceCents?: number },
  ) => {
    try {
      await updateProjectInventoryItem({ id, data: updates }).unwrap()
    } catch (error) {
      console.error('Failed to update item:', error)
    }
  }

  const handleDeleteItem = async (id: string) => {
    try {
      await deleteProjectInventoryItem(id).unwrap()
    } catch (error) {
      console.error('Failed to delete item:', error)
    }
  }

  const handleAddItem = () => {
    setShowAddItemsModal(true)
  }

  const handleExport = async () => {
    try {
      setIsExporting(true)

      // Ask the server to generate the PDF; it uploads to S3 and returns a presigned URL
      const authHeader = getAuthHeader()
      if (!authHeader) {
        logout()
        return
      }
      const response = await fetch(`${API_BASE_URL}/projects/${project.id}/invoice`, {
        headers: {
          Accept: 'application/json',
          Authorization: authHeader,
        },
      })

      if (!response.ok) {
        throw new Error('Failed to generate PDF')
      }

      const { url } = (await response.json()) as { url: string; filename: string }

      // The presigned URL carries Content-Disposition: attachment, so navigating to it
      // downloads the file with the server-chosen filename.
      const link = document.createElement('a')
      link.href = url
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
    } catch (error) {
      console.error('Failed to export PDF:', error)
      // You might want to show a toast notification here
    } finally {
      setIsExporting(false)
    }
  }

  const handleAddInventoryItems = async (
    items: Array<{ inventoryItemId: string; quantity: number }>,
  ) => {
    try {
      await bulkAddProjectInventoryItems({
        projectId: project.id,
        items,
      }).unwrap()
      setShowAddItemsModal(false)
    } catch (error) {
      console.error('Failed to add items:', error)
    }
  }

  const handleCreateItem = async (data: InventoryItemFormData, item: InventoryItemModel | null) => {
    await createInventoryItem(data).unwrap()
  }

  const handleDragStart = (event: DragStartEvent) => {
    setActiveId(event.active.id as string)
  }

  const handleDragEnd = async (event: DragEndEvent) => {
    const { active, over } = event
    setActiveId(null)

    if (!over || active.id === over.id) {
      return
    }

    const oldIndex = items.findIndex((item) => item.id === active.id)
    const newIndex = items.findIndex((item) => item.id === over.id)

    if (oldIndex !== -1 && newIndex !== -1) {
      // Optimistically update the UI immediately
      const reorderedItems = arrayMove(items, oldIndex, newIndex)
      setOptimisticItems(reorderedItems)
      setIsOptimisticUpdate(true)

      // Create order map with item IDs as keys and sort orders as values
      const orderMap = reorderedItems.reduce(
        (acc, item, index) => {
          acc[item.id] = index
          return acc
        },
        {} as Record<string, number>,
      )

      try {
        await updateProjectInventoryItemSortOrder({
          id: project.id,
          data: { orderMap },
        }).unwrap()
        // Don't reset optimistic flag here - let useEffect handle it when fresh data arrives
      } catch (error) {
        console.error('Failed to update sort order:', error)
        // Revert optimistic update on error
        setIsOptimisticUpdate(false)
        setOptimisticItems(baseItems)
      }
    }
  }

  const existingItemIds = items.map((item) => item.inventoryItemId)
  const total = items.reduce((sum, item) => sum + item.currentPrice * item.quantity, 0)
  const totalQuantity = items.reduce((sum, item) => sum + item.quantity, 0)

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
        <CircularProgress />
      </Box>
    )
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mb: 2 }}>
        Failed to load project items. Please try again.
      </Alert>
    )
  }

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="h6">Project Items</Typography>
        <Box display="flex" gap={1}>
          <Button
            startIcon={<FileDownloadIcon />}
            variant="contained"
            onClick={handleExport}
            size="small"
            disabled={isExporting}
          >
            {isExporting ? 'Exporting...' : 'Export'}
          </Button>
          <Button
            startIcon={<AddIcon />}
            variant="contained"
            onClick={handleAddItem}
            size="small"
            disabled={isAdding}
          >
            {isAdding ? 'Adding...' : 'Add Item'}
          </Button>
          <Button
            startIcon={<AddIcon />}
            variant="outlined"
            onClick={() => toggleCreateItemModal(true)}
            size="small"
          >
            Create Item
          </Button>
        </Box>
      </Box>

      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragStart={handleDragStart}
        onDragEnd={handleDragEnd}
        modifiers={[restrictToVerticalAxis]}
      >
        <TableContainer component={Paper} sx={{ position: 'relative' }}>
          <Table>
            <TableHead>
              <TableRow>
                {/* Drag indicator */}
                <TableCell sx={{ width: '50px', padding: '8px' }}></TableCell>
                <TableCell sx={{ width: '80px', padding: '8px' }}>Image</TableCell>
                <TableCell sx={{ padding: '8px' }}>Item Name</TableCell>
                <TableCell sx={{ padding: '8px' }}>Description</TableCell>
                <TableCell sx={{ width: '120px', padding: '8px', textAlign: 'right' }}>
                  Price
                </TableCell>
                <TableCell sx={{ width: '100px', padding: '8px', textAlign: 'right' }}>
                  Quantity
                </TableCell>
                <TableCell sx={{ width: '120px', padding: '8px', textAlign: 'right' }}>
                  Subtotal
                </TableCell>
                {/* Close button */}
                <TableCell sx={{ width: '50px', padding: '8px' }}></TableCell>
              </TableRow>
            </TableHead>
            <SortableContext
              items={items.map((item) => item.id)}
              strategy={verticalListSortingStrategy}
            >
              <TableBody>
                {items.map((item) => (
                  <ProjectItemRow
                    key={item.id}
                    item={item}
                    onUpdateItem={handleUpdateItem}
                    onDeleteItem={handleDeleteItem}
                    isUpdating={isUpdating || isDeleting}
                  />
                ))}
              </TableBody>
            </SortableContext>
            <TableFooter>
              <TableRow>
                <TableCell
                  colSpan={5}
                  sx={{ textAlign: 'right', fontWeight: 'bold', padding: '8px' }}
                >
                  <Typography variant="body1" fontWeight="bold">
                    Total:
                  </Typography>
                </TableCell>
                <TableCell sx={{ fontWeight: 'bold', padding: '8px', textAlign: 'right' }}>
                  <Typography variant="body1" fontWeight="bold">
                    {totalQuantity}
                  </Typography>
                </TableCell>
                <TableCell sx={{ fontWeight: 'bold', padding: '8px', textAlign: 'right' }}>
                  <Typography variant="body1" fontWeight="bold">
                    ${total.toFixed(2)}
                  </Typography>
                </TableCell>
              </TableRow>
            </TableFooter>
          </Table>
        </TableContainer>

        <DragOverlay>
          {activeId ? (
            <Box
              sx={{
                backgroundColor: 'background.paper',
                borderRadius: 1,
                boxShadow: 3,
                opacity: 0.9,
              }}
            >
              <Table>
                <TableBody>
                  {(() => {
                    const draggedItem = items.find((item) => item.id === activeId)
                    return draggedItem ? (
                      <ProjectItemRow
                        item={draggedItem}
                        onUpdateItem={() => {}}
                        onDeleteItem={() => {}}
                        isUpdating={false}
                      />
                    ) : null
                  })()}
                </TableBody>
              </Table>
            </Box>
          ) : null}
        </DragOverlay>
      </DndContext>

      <AddInventoryItemsModal
        open={showAddItemsModal}
        onClose={() => setShowAddItemsModal(false)}
        onAddItems={handleAddInventoryItems}
        existingItemIds={existingItemIds}
      />

      <InventoryItemFormModal
        item={null}
        open={isCreateItemModalOpen}
        onClose={() => toggleCreateItemModal(false)}
        onSubmit={handleCreateItem}
      />
    </Box>
  )
}
